import {
  useEffect,
  useRef,
  lazy,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { resultTypes, resultListClickedItemKey, initListLengths } from '../../appSettings';
import { attemptScrollToCard, getNextResultCount } from '../../utils/helpers';
import { setListCount, getHotels, getAll } from '../../redux/actions/resultsActions';
import LazyLoader from '../lazyLoader/lazyLoader';
import ResultListAll from './resultListAll/resultListAll';
import { logToDataLayer, getSearchEvent } from '../../utils/tracking';
import './resultList.scss';

const ResultListHotels = lazy(() => import(/* webpackChunkName: "result-list-hotels" */ './resultListHotels/resultListHotels'));
const ResultListDestinations = lazy(() => import(/* webpackChunkName: "result-list-destinations" */ './resultListDestinations/resultListDestinations'));
const ResultListExcursions = lazy(() => import(/* webpackChunkName: "result-list-excursions" */ './resultListExcursions/resultListExcursions'));
const ResultListGroupTrips = lazy(() => import(/* webpackChunkName: "result-list-grouptrips" */ './resultListGroupTrips/resultListGroupTrips'));


let storedResultListHeight = null
if (typeof window !== 'undefined') {
  storedResultListHeight = document?.querySelector('.vf-result-container')?.offsetHeight;
}

const ResultList = ({
  filters,
  results,
  onHandleGetHotels,
  onHandleGetAll,
  onSetListCount,
}) => {
  const { loadingAll, listCount, selectedResultHeader } = results;
  // We want to avoid unnecessary re-renders, content is already loaded
  // so first render doesn't need to trigger a fetch
  const firstUpdate = useRef(true);

  // infinite load handler for all result tabs
  const [sentryRef] = useInfiniteScroll({
    hasNextPage: () => results[selectedResultHeader].length > listCount,
    onLoadMore: () => {
      // Endless loop sanity check. If we get so few results that we auto-trigger the infite scroll hook,
      // verify that the result container height has increased since the last trigger
      const domResultListHeight = document.querySelector('.vf-result-container').offsetHeight;
      if (storedResultListHeight === domResultListHeight) {
        return;
      };
      const initCount = initListLengths[selectedResultHeader];
      const maxCount = selectedResultHeader === resultTypes.ALL ? 999 : results.resultsCount[selectedResultHeader];
      const nextListCount = getNextResultCount(listCount, initCount, maxCount);
      onSetListCount(nextListCount);
      storedResultListHeight = document.querySelector('.vf-result-container').offsetHeight;
    },
    rootMargin: '0px 0px 400px 0px', // offset, trigger 400px before it's actually in view
  });

  const getWasLinkTriggered = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('linkTriggered');
  }
  
  useEffect(() => {
    if (firstUpdate.current) return;
    // reset the listCount when changing result list tab
    onSetListCount(initListLengths[selectedResultHeader])
  }, [selectedResultHeader]);
  
  useEffect(() => {
    if (!getWasLinkTriggered()) return;
    const activeElementId = sessionStorage.getItem(resultListClickedItemKey);
    attemptScrollToCard(activeElementId);
  }, []);

  const { 
    selectedSecondaryClassifications,
    selectedHotelProducts,
    selectedLocations,
    selectedFlightTime,
    selectedTemperature,
    selectedTemperaturePeriod,
    pax,
    selectedConceptHotels,
    selectedRatings,
    selectedDepartureMonths,
  } = filters;

  const resetListCount = () => {
    // both variants for browser compability
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    onSetListCount(initListLengths[selectedResultHeader]);
  }

  // Dependencies that only should trigger a fetch of hotels
  // Disable a fetch during first render, will trigger in the useEffect below
  useEffect(() => {
    if (firstUpdate.current) return;
      
    const wasLinkTriggered = getWasLinkTriggered();
    if (!wasLinkTriggered) {
      resetListCount()
    }
    onHandleGetHotels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHotelProducts, pax, selectedConceptHotels, selectedRatings])

  // React to changes to other filter properties.
  useEffect(() => {
    // make sure the next time we trigger a fetch when filters change
    if (firstUpdate.current) firstUpdate.current = false;

    const wasLinkTriggered = getWasLinkTriggered();
    if (!wasLinkTriggered) {
      resetListCount()
    }
    onHandleGetAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSecondaryClassifications,
    selectedLocations,
    selectedFlightTime,
    selectedTemperature,
    selectedTemperaturePeriod,
    selectedDepartureMonths,
  ]);

  // just perform the search event once when landing on the result page
  useEffect(() => logToDataLayer(getSearchEvent(pax, selectedDepartureMonths, results.resultsCount.totalHits)), []);

  const getResultContent = () => {
    switch (selectedResultHeader) {
      case resultTypes.ALL:
        return (
          <ResultListAll
            mixListCount={listCount}
            hotels={results.hotels}
            destinations={results.destinations}
            excursions={results.excursions}
            grouptrips={results.grouptrips}
            articles={results.articles}/>
        )
      case resultTypes.HOTELS:
        return (
          <LazyLoader loadingAll={loadingAll}>
            <ResultListHotels
              listCount={listCount}
              hotels={results.hotels} 
            />
          </LazyLoader>
        )
      case resultTypes.DESTINATIONS:
        return (
          <LazyLoader loadingAll={loadingAll}>
            <ResultListDestinations
              listCount={listCount}
              destinations={results.destinations}
            />
          </LazyLoader>
        )
      case resultTypes.EXCURSIONS:
        return (
          <LazyLoader loadingAll={loadingAll}>
            <ResultListExcursions
              listCount={listCount}
              excursions={results.excursions}
            />
          </LazyLoader>
        )
      case resultTypes.GROUP_TRIPS:
        return (
          <LazyLoader loadingAll={loadingAll}>
            <ResultListGroupTrips
              listCount={listCount} 
              grouptrips={results.grouptrips}
            />
          </LazyLoader>
        )
      default:
        return <h2>Error: Invalid result type set</h2>
    }
  }

  return (
    <section className='vf-result-container'>
      { getResultContent() }

      {/* When this guy comes into the viewport (offset by 400px) the infinite load hook gets triggered */}
      <div ref={sentryRef} />
    </section>
  )
}

ResultList.propTypes = {
  results: PropTypes.shape({
    resultsCount: PropTypes.shape({
      total: PropTypes.number,
    }),
    loadingAll: PropTypes.bool.isRequired,
    listCount: PropTypes.number.isRequired,
    selectedResultHeader: PropTypes.string.isRequired,
    hotels: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    destinations: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    excursions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    grouptrips: PropTypes.arrayOf(PropTypes.shape()).isRequired,
    articles: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  filters: PropTypes.shape({
    selectedSecondaryClassifications: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedHotelProducts: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedLocations: PropTypes.arrayOf(PropTypes.string),
    selectedFlightTime: PropTypes.string,
    selectedTemperature: PropTypes.string,
    selectedTemperaturePeriod: PropTypes.string,
    selectedConceptHotels: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedRatings: PropTypes.arrayOf(PropTypes.string).isRequired,
    selectedDepartureMonths: PropTypes.arrayOf(PropTypes.string),
    pax: PropTypes.shape({}).isRequired,
  }).isRequired,
  onSetListCount: PropTypes.func.isRequired,
  onHandleGetHotels: PropTypes.func.isRequired,
  onHandleGetAll: PropTypes.func.isRequired,
}

const mapDispatchToProps = (dispatch) => {
  return {
    onHandleGetHotels: () => {
      dispatch(getHotels());
    },
    onHandleGetAll: () => {
      dispatch(getAll());
    },
    onSetListCount: (listCount) => {
      dispatch(setListCount(listCount));
    },
  };
};

const mapStateToProps = (state) => {
  return {
    results: state.results,
    filters: state.siteSettings.filters,
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ResultList);
